import { useState, useMemo, useEffect, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, Avatar,
  Divider, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Pagination, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Tabs, Tab, Tooltip, IconButton,
} from '@mui/material';
import {
  CheckCircle, Cancel, PhoneAndroid, Refresh, AttachMoney,
} from '@mui/icons-material';
import {
  LIST_ADMIN_CASHOUT_REQUESTS,
  CONFIRM_ADMIN_CASHOUT,
  REJECT_ADMIN_CASHOUT,
  GET_DRIVER_BALANCE,
  RECORD_COMMISSION_PAYMENT,
  RUN_BALANCE_ENFORCEMENT,
} from '../graphql/queries';
import type { CashoutRequest, CashoutStatus } from '../mocks/cashoutMock';

const client = generateClient();

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_COLOR: Record<CashoutStatus, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
  PENDING:    'warning',
  APPROVED:   'info',
  PROCESSING: 'info',
  COMPLETED:  'success',
  FAILED:     'error',
  REJECTED:   'error',
  CANCELLED:  'default',
};

const PROVIDER_LABEL: Record<string, string> = {
  MTN_MOBILE_MONEY: 'MTN MoMo',
  ORANGE_MONEY:     'Orange Money',
};

const PROVIDER_COLOR: Record<string, string> = {
  MTN_MOBILE_MONEY: '#FFCC00',
  ORANGE_MONEY:     '#FF6600',
};

/** Human label for the payout method of a request (Cash or a mobile money provider). */
function methodLabel(req: CashoutRequest): string {
  if (req.payoutMethodType === 'CASH') return 'Cash';
  return PROVIDER_LABEL[req.mobileMoneyProvider ?? ''] ?? 'Mobile Money';
}

function fmt(amount: number, currency: string) {
  return `${currency} ${amount.toLocaleString()}`;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

function SummaryCards({ requests }: { requests: CashoutRequest[] }) {
  const pending   = requests.filter(r => r.status === 'PENDING');
  const completed = requests.filter(r => r.status === 'COMPLETED');
  const failed    = requests.filter(r => r.status === 'FAILED' || r.status === 'REJECTED');
  const totalPendingAmt = pending.reduce((s, r) => s + r.amount, 0);

  const cards = [
    { label: 'Pending Requests', value: pending.length,   sub: fmt(totalPendingAmt, 'LRD'), color: '#ed6c02' },
    { label: 'Completed Today',  value: completed.length, sub: fmt(completed.reduce((s,r)=>s+r.amount,0),'LRD'), color: '#2e7d32' },
    { label: 'Failed / Rejected',value: failed.length,    sub: '',  color: '#d32f2f' },
    { label: 'Total Requests',   value: requests.length,  sub: '',  color: '#1565c0' },
  ];

  return (
    <Grid container spacing={2} mb={3}>
      {cards.map(c => (
        <Grid item xs={6} md={3} key={c.label}>
          <Card>
            <CardContent sx={{ pb: '16px !important' }}>
              <Typography variant="body2" color="textSecondary">{c.label}</Typography>
              <Typography variant="h4" fontWeight={700} color={c.color}>{c.value}</Typography>
              {c.sub && <Typography variant="caption" color="textSecondary">{c.sub}</Typography>}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CashoutManagement() {
  const [requests, setRequests]           = useState<CashoutRequest[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [tab, setTab]                     = useState(0);
  const [filterMethod, setFilterMethod]   = useState('ALL');
  const [page, setPage]                   = useState(1);
  const [selected, setSelected]           = useState<CashoutRequest | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason]     = useState('');
  const [externalReference, setExternalReference] = useState('');
  const [processing, setProcessing]       = useState(false);
  const [successMsg, setSuccessMsg]       = useState('');
  // Driver balance & settlement
  const [balanceDriverId, setBalanceDriverId] = useState('');
  const [balance, setBalance]             = useState<any | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payAmount, setPayAmount]         = useState('');
  const [payRef, setPayRef]               = useState('');
  const [enforcing, setEnforcing]         = useState(false);
  const PER_PAGE = 8;

  const loadBalance = async (driverId: string) => {
    const id = driverId.trim();
    if (!id) return;
    setBalanceLoading(true);
    setBalance(null);
    try {
      const result: any = await client.graphql({ query: GET_DRIVER_BALANCE, variables: { driverId: id } });
      setBalance(result.data.getDriverBalance);
    } catch (err: any) {
      setError(err.message || 'Failed to load driver balance');
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    const amt = parseFloat(payAmount);
    if (!balance || !amt || amt <= 0) return;
    setProcessing(true);
    try {
      const result: any = await client.graphql({
        query: RECORD_COMMISSION_PAYMENT,
        variables: { driverId: balance.driverId, amount: amt, reference: payRef.trim() || null, adminName: 'Admin User' },
      });
      setBalance(result.data.recordCommissionPayment);
      setSuccessMsg(`Recorded LRD ${amt.toLocaleString()} commission payment.`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to record payment');
    } finally {
      setProcessing(false);
      setPayDialogOpen(false);
      setPayAmount('');
      setPayRef('');
    }
  };

  const handleRunEnforcement = async () => {
    setEnforcing(true);
    try {
      const result: any = await client.graphql({ query: RUN_BALANCE_ENFORCEMENT });
      const r = result.data.runBalanceEnforcement;
      setSuccessMsg(`Enforcement ran: ${r.negativeCount} negative, ${r.newlyDisabled} disabled, ${r.reactivated} reactivated.`);
      setTimeout(() => setSuccessMsg(''), 6000);
    } catch (err: any) {
      setError(err.message || 'Enforcement failed');
    } finally {
      setEnforcing(false);
    }
  };

  const tabStatuses: (CashoutStatus | 'ALL')[] = ['ALL', 'PENDING', 'COMPLETED', 'FAILED'];

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusFilter = tab === 0 ? undefined : tabStatuses[tab];
      const result: any = await client.graphql({
        query: LIST_ADMIN_CASHOUT_REQUESTS,
        variables: { status: statusFilter, limit: 200 },
      });
      setRequests(result.data.listAdminCashoutRequests.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load cashout requests');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const filtered = useMemo(() => {
    return requests.filter(r => {
      const statusMatch = tab === 0 ? true : r.status === tabStatuses[tab];
      const methodMatch = filterMethod === 'ALL' ? true : r.payoutMethodType === filterMethod;
      return statusMatch && methodMatch;
    });
  }, [requests, tab, filterMethod]);

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  // Confirm (payout already sent externally)
  const handleConfirm = async () => {
    if (!selected) return;
    setProcessing(true);
    try {
      const result: any = await client.graphql({
        query: CONFIRM_ADMIN_CASHOUT,
        variables: {
          requestId: selected.requestId,
          adminName: 'Admin User',
          externalReference: externalReference.trim() || null,
        },
      });
      const updated = result.data.confirmAdminCashout;
      setRequests(prev => prev.map(r => r.requestId === selected.requestId ? { ...r, ...updated } : r));
      setSuccessMsg(`Cashout confirmed as paid${updated.externalReference ? ` (ref: ${updated.externalReference})` : ''}.`);
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err: any) {
      setError(err.message || 'Confirmation failed');
    } finally {
      setProcessing(false);
      setApproveDialogOpen(false);
      setExternalReference('');
      setSelected(null);
    }
  };

  // Reject
  const handleReject = async () => {
    if (!selected || !rejectionReason.trim()) return;
    setProcessing(true);
    try {
      const result: any = await client.graphql({
        query: REJECT_ADMIN_CASHOUT,
        variables: { requestId: selected.requestId, adminName: 'Admin User', reason: rejectionReason },
      });
      const updated = result.data.rejectAdminCashout;
      setRequests(prev => prev.map(r => r.requestId === selected.requestId ? { ...r, ...updated } : r));
    } catch (err: any) {
      setError(err.message || 'Rejection failed');
    } finally {
      setProcessing(false);
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelected(null);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>Cashout Management</Typography>
          <Typography variant="body2" color="textSecondary">
            Review driver cashout requests. Send the payout via cash or mobile money, then confirm or reject here.
          </Typography>
        </Box>
        <Tooltip title="Runs the negative-balance sweep now (also runs nightly)">
          <span>
            <Button variant="outlined" onClick={handleRunEnforcement} disabled={enforcing}
              startIcon={enforcing ? <CircularProgress size={16} /> : <Refresh />}>
              Run Enforcement
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Driver balance & settlement */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Driver Balance & Settlement</Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={balance ? 2 : 0}>
            <TextField size="small" label="Driver ID" value={balanceDriverId}
              onChange={e => setBalanceDriverId(e.target.value)} sx={{ minWidth: 320 }}
              onKeyDown={e => { if (e.key === 'Enter') loadBalance(balanceDriverId); }} />
            <Button variant="contained" onClick={() => loadBalance(balanceDriverId)} disabled={balanceLoading || !balanceDriverId.trim()}>
              {balanceLoading ? <CircularProgress size={20} /> : 'Load Balance'}
            </Button>
          </Box>
          {balance && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Net Balance</Typography>
                  <Typography variant="h6" fontWeight={700} color={balance.netBalance < 0 ? 'error.main' : 'success.main'}>
                    {fmt(balance.netBalance, 'LRD')}
                  </Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Card Earnings Due</Typography>
                  <Typography variant="body2">{fmt(balance.cardEarningsDue, 'LRD')}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Cash Commission Owed</Typography>
                  <Typography variant="body2">{fmt(balance.cashCommissionOwed, 'LRD')}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Commission Settled</Typography>
                  <Typography variant="body2">{fmt(balance.commissionSettled, 'LRD')}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Cash Collected</Typography>
                  <Typography variant="body2">{fmt(balance.cashCollected, 'LRD')}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Total Cashed Out</Typography>
                  <Typography variant="body2">{fmt(balance.totalCashedOut, 'LRD')}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Lifetime Earnings</Typography>
                  <Typography variant="body2">{fmt(balance.lifetimeEarnings, 'LRD')}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption" color="textSecondary">Negative Since</Typography>
                  <Typography variant="body2" color={balance.negativeSince ? 'error.main' : 'textSecondary'}>
                    {balance.negativeSince ? new Date(balance.negativeSince).toLocaleDateString() : '—'}
                  </Typography></Grid>
              </Grid>
              {balance.amountOwed > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Driver owes {fmt(balance.amountOwed, 'LRD')} in commission on collected cash.
                </Alert>
              )}
              <Box mt={2}>
                <Button variant="contained" color="success" onClick={() => { setPayAmount(''); setPayRef(''); setPayDialogOpen(true); }}>
                  Record Commission Payment
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      <SummaryCards requests={requests} />

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(1); }}>
              <Tab label="All" />
              <Tab label={`Pending (${requests.filter(r=>r.status==='PENDING').length})`} />
              <Tab label="Completed" />
              <Tab label="Failed / Rejected" />
            </Tabs>
            <Box flexGrow={1} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Method</InputLabel>
              <Select value={filterMethod} onChange={e => { setFilterMethod(e.target.value); setPage(1); }} label="Method">
                <MenuItem value="ALL">All Methods</MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="MOBILE_MONEY">Mobile Money</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchRequests}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Driver ID</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Ref</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">No requests found</Typography>
                </TableCell>
              </TableRow>
            )}
            {paginated.map(req => (
              <TableRow key={req.requestId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{req.driverId?.slice(0, 8)}...</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>{fmt(req.amount, req.currency)}</Typography>
                </TableCell>
                <TableCell>
                  {req.payoutMethodType === 'CASH' ? (
                    <Chip label="Cash" size="small" icon={<AttachMoney />}
                      sx={{ bgcolor: '#e0e0e0', color: '#000', fontWeight: 600 }} />
                  ) : (
                    <Chip
                      label={methodLabel(req)}
                      size="small"
                      icon={<PhoneAndroid />}
                      sx={{ bgcolor: PROVIDER_COLOR[req.mobileMoneyProvider ?? ''] ?? '#ccc', color: '#000', fontWeight: 600 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {req.payoutMethodType === 'CASH' ? 'In person' : (req.mobileMoneyNumber ?? '—')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={req.payoutType === 'WEEKLY' ? 'Weekly' : 'On-demand'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Tooltip title={new Date(req.requestedAt).toLocaleString()}>
                    <Typography variant="body2">{timeAgo(req.requestedAt)}</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip label={req.status} color={STATUS_COLOR[req.status]} size="small" />
                </TableCell>
                <TableCell>
                  {req.transactionId ? (
                    <Tooltip title={req.transactionId}>
                      <Typography variant="caption" sx={{ cursor: 'pointer', color: 'primary.main' }}
                        onClick={() => navigator.clipboard.writeText(req.transactionId!)}>
                        {req.transactionId.slice(-8)}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="caption" color="textSecondary">—</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {req.status === 'PENDING' && (
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button size="small" variant="contained" color="success"
                        startIcon={<CheckCircle />}
                        onClick={() => { setSelected(req); setExternalReference(''); setApproveDialogOpen(true); }}>
                        Confirm
                      </Button>
                      <Button size="small" variant="outlined" color="error"
                        startIcon={<Cancel />}
                        onClick={() => { setSelected(req); setRejectDialogOpen(true); }}>
                        Reject
                      </Button>
                    </Box>
                  )}
                  {req.status === 'REJECTED' && req.rejectionReason && (
                    <Tooltip title={req.rejectionReason}>
                      <Typography variant="caption" color="error" sx={{ cursor: 'help' }}>
                        View reason
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
        </Box>
      )}

      {/* Confirm Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => !processing && setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Cashout Payout</DialogTitle>
        <DialogContent>
          {selected && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Confirm only after you have sent the payout to the driver
                {selected.payoutMethodType === 'CASH'
                  ? ' in cash'
                  : ` to ${methodLabel(selected)} ${selected.mobileMoneyNumber ?? ''}`}. This marks the request as paid.
              </Alert>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography variant="caption" color="textSecondary">Driver ID</Typography>
                    <Typography variant="body2" fontWeight={600}>{selected.driverId}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="textSecondary">Amount</Typography>
                    <Typography variant="body2" fontWeight={700} color="success.main">{fmt(selected.amount, selected.currency)}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="textSecondary">Method</Typography>
                    <Typography variant="body2">{methodLabel(selected)}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="textSecondary">Destination</Typography>
                    <Typography variant="body2">{selected.payoutMethodType === 'CASH' ? 'In person' : (selected.mobileMoneyNumber ?? '—')}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="textSecondary">Payout Timing</Typography>
                    <Typography variant="body2">{selected.payoutType === 'WEEKLY' ? 'Weekly' : 'On-demand'}</Typography></Grid>
                </Grid>
              </Box>
              <TextField
                fullWidth
                label="External transfer reference (optional)"
                placeholder="e.g. MTN txn ID, receipt no., or note"
                value={externalReference}
                onChange={e => setExternalReference(e.target.value)}
                disabled={processing}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setApproveDialogOpen(false); setExternalReference(''); }} disabled={processing}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="success" disabled={processing}>
            {processing ? <CircularProgress size={20} /> : 'Confirm as Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => !processing && setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Cashout Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Provide a reason — this will be shown to the driver.
          </Typography>
          <TextField fullWidth multiline rows={3} value={rejectionReason}
            onChange={e => setRejectionReason(e.target.value)}
            placeholder="e.g. Disputed trip earnings under review..."
            sx={{ mt: 2 }} disabled={processing} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained"
            disabled={!rejectionReason.trim() || processing}>
            {processing ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Commission Payment Dialog */}
      <Dialog open={payDialogOpen} onClose={() => !processing && setPayDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Commission Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Record a commission payment the driver made externally (mobile money, bank, or office).
            This credits their ledger.
          </Typography>
          <TextField fullWidth type="number" label="Amount (LRD)" value={payAmount}
            onChange={e => setPayAmount(e.target.value)} sx={{ mt: 2 }} disabled={processing} />
          <TextField fullWidth label="Reference (optional)" value={payRef}
            onChange={e => setPayRef(e.target.value)} placeholder="e.g. MTN txn ID, receipt no."
            sx={{ mt: 2 }} disabled={processing} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" color="success"
            disabled={processing || !(parseFloat(payAmount) > 0)}>
            {processing ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
